import { BlockPadding, Curtain } from "@business-finland/wif-ui-lib";
import { SectionHeader } from "@business-finland/wif-ui-lib/dist/SectionHeader";
import { JobList } from "@business-finland/wif-ui-lib/dist/JobList";
import { Pagination } from "@business-finland/wif-ui-lib/dist/Pagination";
import {
  RefObject,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { useTranslation } from "next-i18next";

import {
  OpenJobsDispatchContext,
  OpenJobsStateContext,
  useOpenJobsDispatch,
  useOpenJobsState,
} from "../../templates/OpenJobsPage/OpenJobs/context";
import {
  ACTIONS,
  openJobsInitialState,
  openJobsStateReducer,
} from "../../templates/OpenJobsPage/OpenJobs/store";
import OpenJobsHeader from "../../templates/OpenJobsPage/OpenJobs/OpenJobsHeader";
import useOpenJobsSearch from "../../templates/OpenJobsPage/OpenJobs/useOpenJobsSearch";
import {
  headerHeightSmAndAbove,
  headerHeightXs,
  Viewport,
} from "../../../constants/app";
import useViewport from "../../../contexts/ViewportContext";
import { getJobsPageSize, JOBS_PREVIEW_COUNT } from "../../../util/jobs-list";
import {
  resolveJobsPageBackgroundColor,
  uiBackgroundColorHex,
} from "../../../util/cms-background-color";

export interface IJobsModuleProps {
  heading: string;
  leadText: string;
  anchorTag?: string;
  categories?: string[];
  campaignId?: string;
  showJobLogos?: boolean;
  backgroundColor?: "white" | "color";
}

export function JobsModule(props: IJobsModuleProps): JSX.Element {
  const [state, dispatch] = useReducer(
    openJobsStateReducer,
    openJobsInitialState
  );

  useEffect(() => {
    dispatch(ACTIONS.initState(props.categories ?? []));
  }, [dispatch, props.categories]);

  const { t } = useTranslation("common");
  const { isMobile } = useViewport();
  const pageSize = getJobsPageSize(isMobile);

  useOpenJobsSearch(state, dispatch, props.campaignId, pageSize);

  const pageBackgroundColor = resolveJobsPageBackgroundColor(
    props.backgroundColor
  );
  const [isExpanded, setIsExpanded] = useState(false);
  const [filtersVisible, setFiltersVisible] = useState(false);

  const jobCardsSectionRef = useRef<HTMLDivElement>(null);

  const { isError, isCurtainVisible, totalJobs, jobCards, page } = state;
  const isPreview = !isMobile && !isExpanded;
  const visibleCount = isPreview ? JOBS_PREVIEW_COUNT : pageSize;
  const isEmpty = isError || Boolean(jobCards !== undefined && totalJobs === 0);

  const previewI18n = useMemo(
    () => ({
      showFilters: t("jobs.showFilters"),
      hideFilters: t("jobs.hideFilters"),
      resetFilters: t("jobs.resetFilters"),
      showJobs: t("jobs.showJobs", { count: totalJobs }),
      filtersTitle: t("jobs.filtersTitle"),
      close: t("jobs.close"),
      searchLabel: t("jobs.searchLabel"),
      searchJobs: t("jobs.searchJobs", { count: totalJobs }),
      categoryLabel: t("jobs.categoryLabel"),
      cityLabel: t("jobs.cityLabel"),
    }),
    [t, totalJobs]
  );

  return (
    <OpenJobsDispatchContext.Provider value={dispatch}>
      <OpenJobsStateContext.Provider value={state}>
        <div
          id={props.anchorTag || undefined}
          style={{
            backgroundColor: uiBackgroundColorHex(
              pageBackgroundColor,
              "#f5f5f5"
            ),
          }}
        >
          <OpenJobsHeader
            {...props}
            layout="with-filters"
            filtersVisible={filtersVisible}
            onToggleFilters={() => setFiltersVisible((open) => !open)}
            onResetFilters={() =>
              dispatch(
                ACTIONS.updateStateWithPageReset({
                  query: "",
                  selectedCategories: [],
                  selectedCities: [],
                })
              )
            }
            headerI18n={previewI18n}
          />

          <div
            ref={jobCardsSectionRef}
            style={{ position: "relative", zIndex: 0 }}
          >
            <Curtain show={isCurtainVisible} />

            {isEmpty && <SectionHeader>0 jobs available</SectionHeader>}

            {!isEmpty && (
              <JobList
                visibleCount={visibleCount}
                totalJobs={totalJobs}
                page={page}
                pageSize={pageSize}
                formatRange={(from: number, to: number, total: number) =>
                  t("jobs.showingRange", { from, to, total })
                }
                showRange
                showMoreVisible={isPreview && totalJobs > JOBS_PREVIEW_COUNT}
                showMoreText={t("jobs.showMoreJobs")}
                onShowMore={() => {
                  setIsExpanded(true);
                  setFiltersVisible(true);
                }}
                bottomPadding={BlockPadding.SMALL}
                jobCards={jobCards}
                showLogo={props.showJobLogos !== false}
                backgroundColor={pageBackgroundColor}
                skeletonLoadingCount={isPreview ? JOBS_PREVIEW_COUNT : pageSize}
              />
            )}

            {(isExpanded || isMobile) && (
              <JobsModulePagination
                jobCardsSectionRef={jobCardsSectionRef}
                caption={
                  isMobile && totalJobs > 0
                    ? t("jobs.showingRange", {
                      from: (page - 1) * pageSize + 1,
                      to: Math.min(page * pageSize, totalJobs),
                      total: totalJobs,
                    })
                    : undefined
                }
              />
            )}
          </div>
        </div>
      </OpenJobsStateContext.Provider>
    </OpenJobsDispatchContext.Provider>
  );
}

function JobsModulePagination(props: {
  jobCardsSectionRef: RefObject<HTMLDivElement>;
  caption?: string;
}): JSX.Element | null {
  const { totalPages, page } = useOpenJobsState();
  const dispatch = useOpenJobsDispatch();
  const { viewport } = useViewport();

  const scrollToJobCardsStart = () => {
    if (props.jobCardsSectionRef.current) {
      const offsetJobCardSection = props.jobCardsSectionRef.current.offsetTop;
      const headerHeight =
        viewport === Viewport.xs ? headerHeightXs : headerHeightSmAndAbove;

      const offsetTotal = offsetJobCardSection - headerHeight;

      window.scrollTo(0, offsetTotal);
    }
  };
  const onPagination = (page: number) => {
    dispatch({ page });
    scrollToJobCardsStart();
  };

  if (totalPages === 0) {
    return null;
  }

  return (
    <div style={props.caption ? { paddingTop: 20 } : undefined}>
      <Pagination
        count={totalPages}
        onChange={onPagination}
        value={page}
        caption={props.caption}
      />
    </div>
  );
}
